import React, { FC, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { CalendarIcon } from '../../assets/icons/index';
import { Calendar, CalendarProps } from '../calendar/index';
import { Input } from '../input/index';
import { Popover, PopoverContent, PopoverTrigger } from '../popover/index';

type FormDatePickerProps = {
  calendar?: Except<CalendarProps, 'initialFocus' | 'mode' | 'selected'>;
  defaultMonth?: Date;
  disabled?: boolean;
  disabledInput?: boolean;
  /** Drops the calendar icon — for a read-only field, where it would advertise a picker. */
  hideIcon?: boolean;
  iconColor?: string;
  inputClassName?: string;
  inputValue: string;
  inputWrapperClassName?: string;
  isDefaultOpenCalendar?: boolean;
  onSelect?: (date: Date) => void;
  placeholder?: ReactNode | string;
  value?: Date | null;
  wrapperClassName?: string;
};

export const FormDatePicker: FC<FormDatePickerProps> = ({
  onSelect,
  value,
  isDefaultOpenCalendar,
  wrapperClassName,
  calendar,
  inputValue,
  inputWrapperClassName,
  placeholder,
  iconColor = '#344054',
  defaultMonth,
  disabled,
  disabledInput,
  hideIcon,
  inputClassName,
}) => {
  const [isOpenCalendar, setOpenCalendar] = useState(isDefaultOpenCalendar ?? false);

  return (
    <Popover onOpenChange={setOpenCalendar} open={isOpenCalendar}>
      <PopoverTrigger className={twMerge('relative w-full cursor-pointer', wrapperClassName)}>
        <div
          className="absolute z-50 h-full w-full"
          onClick={() => setOpenCalendar((prev) => !prev)}
        />
        <Input
          autoComplete="off"
          disabled={disabled}
          disabledInput={disabledInput}
          icon={
            hideIcon ? undefined : <CalendarIcon className="mr-2 size-6" iconColor={iconColor} />
          }
          inputClassName={inputClassName}
          onChange={() => ''}
          onIconClick={() => setOpenCalendar(true)}
          placeholder={placeholder}
          value={inputValue}
          wrapperClassName={inputWrapperClassName}
        />
      </PopoverTrigger>
      <PopoverContent className="z-[100] w-auto p-0">
        <Calendar
          defaultMonth={defaultMonth}
          disabled={disabled || disabledInput}
          {...calendar}
          initialFocus
          mode="single"
          onSelect={(date) => {
            if (!date) return;
            setOpenCalendar(false);
            onSelect?.(date);
          }}
          selected={value || undefined}
        />
      </PopoverContent>
    </Popover>
  );
};
